/**
 * Widget Module TypeScript Types
 * ===============================
 * Type definitions for Website Widget Module
 *
 * Added: 2025-11-17 - Website Widget Module Phase 1
 */

// ============================================================================
// Enums
// ============================================================================

export enum WidgetPosition {
  LEFT = 'left',
  RIGHT = 'right',
}

export enum WidgetStyle {
  STANDARD = 'standard',
  EXPANDED = 'expanded',
  COMPACT = 'compact',
}

export enum DarkMode {
  LIGHT = 'light',
  DARK = 'dark',
  AUTO = 'auto',
}

export enum AttributeType {
  TEXT = 'text',
  EMAIL = 'email',
  NUMBER = 'number',
  SELECT = 'select',
  TEXTAREA = 'textarea',
  PHONE = 'phone',
}

export enum CSATRating {
  VERY_DISSATISFIED = '1',
  DISSATISFIED = '2',
  NEUTRAL = '3',
  SATISFIED = '4',
  VERY_SATISFIED = '5',
}

// ============================================================================
// Pre-chat Form Types
// ============================================================================

export interface PreChatFormField {
  name: string;
  label: string;
  type: AttributeType;
  required: boolean;
  options?: string[];
  placeholder?: string;
}

export interface BusinessHours {
  enabled: boolean;
  from?: string; // HH:MM format
  to?: string; // HH:MM format
}

export type BusinessHoursConfig = {
  [key in 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday']?: BusinessHours;
};

// ============================================================================
// Widget Configuration Types
// ============================================================================

export interface WidgetConfigCreate {
  // Appearance
  widget_color?: string;
  position?: WidgetPosition;
  widget_style?: WidgetStyle;
  launcher_title?: string;

  // Welcome Messages
  welcome_heading?: string;
  welcome_tagline?: string;

  // Pre-chat Form
  pre_chat_form_enabled?: boolean;
  pre_chat_form_message?: string;
  pre_chat_form_fields?: PreChatFormField[];

  // Business Hours
  business_hours_enabled?: boolean;
  business_hours?: BusinessHoursConfig;
  timezone?: string;
  offline_message?: string;

  // Features
  email_collect_enabled?: boolean;
  csat_enabled?: boolean;
  auto_greeting_enabled?: boolean;
  auto_greeting_message?: string;

  // Security
  hmac_enabled?: boolean;
  allowed_domains?: string[];

  // Advanced
  dark_mode?: DarkMode;
  locale?: string;
  reply_time?: string;
}

export interface WidgetConfigUpdate extends Partial<WidgetConfigCreate> {}

export interface WidgetConfigResponse {
  id: number;
  channel_id: number;

  // Appearance
  widget_color: string;
  position: string;
  widget_style: string;
  launcher_title: string;

  // Welcome Messages
  welcome_heading: string;
  welcome_tagline: string;

  // Pre-chat Form
  pre_chat_form_enabled: boolean;
  pre_chat_form_message?: string;
  pre_chat_form_fields: any[];

  // Business Hours
  business_hours_enabled: boolean;
  business_hours: BusinessHoursConfig;
  timezone: string;
  offline_message?: string;

  // Features
  email_collect_enabled: boolean;
  csat_enabled: boolean;
  auto_greeting_enabled: boolean;
  auto_greeting_message?: string;

  // Security
  hmac_enabled: boolean;

  // Advanced
  dark_mode: string;
  locale: string;
  reply_time: string;

  created_at: string;
  updated_at: string;
}

export interface WidgetConfigAdminResponse extends WidgetConfigResponse {
  account_id: number;
  hmac_secret?: string;
  allowed_domains: string[];
}

// ============================================================================
// Widget Session Types
// ============================================================================

export interface WidgetSessionCreate {
  channel_id: number;
  user_identifier?: string;
  user_identifier_hash?: string;
  ip_address?: string;
  user_agent?: string;
  browser_language?: string;
  referrer_url?: string;
  current_url?: string;
  custom_attributes?: Record<string, any>;
}

export interface WidgetSessionResponse {
  id: number;
  channel_id: number;
  session_token: string;
  contact_id?: number;
  conversation_id?: number;
  user_identifier?: string;
  custom_attributes: Record<string, any>;
  is_active: boolean;
  last_activity_at: string;
  created_at: string;
}

// ============================================================================
// Widget Initialization Types
// ============================================================================

export interface WidgetInitRequest {
  website_token: string;
  user_identifier?: string;
  user_identifier_hash?: string;
  custom_attributes?: Record<string, any>;
  referrer_url?: string;
  current_url?: string;
}

export interface WidgetInitResponse {
  session_token: string;
  config: WidgetConfigResponse;
  contact_id?: number;
  conversation_id?: number;
  pubsub_token?: string;
}

// ============================================================================
// Pre-chat Form Submission Types
// ============================================================================

export interface PreChatFormSubmission {
  session_token: string;
  fields: Record<string, any>;
  email?: string;
  name?: string;
  phone?: string;
}

export interface PreChatFormResponse {
  success: boolean;
  contact_id: number;
  conversation_id?: number;
  message: string;
}

// ============================================================================
// CSAT Types
// ============================================================================

export interface CSATSubmission {
  session_token: string;
  conversation_id: number;
  rating: CSATRating;
  feedback?: string;
}

export interface CSATResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// Installation Code Types
// ============================================================================

export interface WidgetInstallationCodeResponse {
  channel_id: number;
  website_token: string;
  installation_code: string;
}

// ============================================================================
// Custom Attribute Types
// ============================================================================

export interface WidgetCustomAttributeCreate {
  attribute_key: string;
  attribute_display_name: string;
  attribute_type: AttributeType;
  attribute_description?: string;
  attribute_values?: string[];
  show_in_pre_chat: boolean;
  required_in_pre_chat: boolean;
  display_order: number;
  regex_pattern?: string;
  regex_error_message?: string;
}

export interface WidgetCustomAttributeUpdate extends Partial<Omit<WidgetCustomAttributeCreate, 'attribute_key'>> {}

export interface WidgetCustomAttributeResponse {
  id: number;
  account_id: number;
  attribute_key: string;
  attribute_display_name: string;
  attribute_type: string;
  attribute_description?: string;
  attribute_values?: string[];
  show_in_pre_chat: boolean;
  required_in_pre_chat: boolean;
  display_order: number;
  regex_pattern?: string;
  regex_error_message?: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// WebSocket Message Types
// ============================================================================

export interface WidgetWebSocketMessage {
  type: 'message' | 'typing' | 'read' | 'ping';
  content?: string;
  email?: string;
  name?: string;
  is_typing?: boolean;
  message_id?: number;
}

export interface WidgetWebSocketResponse {
  type: 'connected' | 'message_sent' | 'new_message' | 'typing' | 'message_read' | 'pong' | 'error';
  data?: any;
  error?: string;
}

// ============================================================================
// Error Response Types
// ============================================================================

export interface WidgetErrorResponse {
  error: string;
  code?: string;
  details?: Record<string, any>;
}

// ============================================================================
// UI State Types (for React components)
// ============================================================================

export interface WidgetSettingsFormData extends WidgetConfigCreate {
  // Additional UI-specific fields if needed
}

export interface WidgetPreviewState {
  position: WidgetPosition;
  color: string;
  style: WidgetStyle;
  isOpen: boolean;
  showPreChat: boolean;
  darkMode: DarkMode;
}

export interface BusinessHoursFormData {
  [key: string]: {
    enabled: boolean;
    from: string;
    to: string;
  };
}
